import React, { useState, useMemo } from 'react';
import {
  ClipboardDocumentListIcon,
  MapPinIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import MaintenanceRequestCard, {
  MaintenanceRequest
} from './MaintenanceRequestCard';
import PropertyMaintenanceSummaryCard from './PropertyMaintenanceSummaryCard';
import { Listing as Property } from '../../property/property-card/PropertyCardTypes';

interface LandlordMaintenanceViewProps {
  isLoading: boolean;
  requests: MaintenanceRequest[];
  filteredRequests: MaintenanceRequest[];
  handleStatusChange: (id: string, status: string) => void;
  handleDeleteRequest: (id: string) => void;
  isUpdating: string | null;
  isDeleting: string | null;
  properties: Property[];
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  page?: number;
  hasNext?: boolean;
  onPageChange?: (targetPage: number) => void;
  isLoadingPage?: boolean;
}

const LandlordMaintenanceView: React.FC<LandlordMaintenanceViewProps> = ({
  isLoading,
  requests,
  filteredRequests,
  handleStatusChange,
  handleDeleteRequest,
  isUpdating,
  isDeleting,
  properties,
  statusFilter,
  setStatusFilter,
  searchTerm,
  setSearchTerm,
  page = 1,
  hasNext = false,
  onPageChange,
  isLoadingPage = false
}) => {
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(
    null
  );

  const groupedRequests = useMemo(() => {
    return requests.reduce(
      (acc, req) => {
        const property = properties.find((p) => p.id === Number(req.listingId));
        if (!property) return acc;

        if (!acc[property.id]) {
          acc[property.id] = {
            property,
            requests: [],
            pendingCount: 0,
            inProgressCount: 0
          };
        }

        acc[property.id].requests.push(req);
        if (req.status === 'pending') {
          acc[property.id].pendingCount++;
        }
        if (req.status === 'in-progress') {
          acc[property.id].inProgressCount++;
        }

        return acc;
      },
      {} as Record<
        string,
        {
          property: Property;
          requests: MaintenanceRequest[];
          pendingCount: number;
          inProgressCount: number;
        }
      >
    );
  }, [requests, properties]);

  const handlePropertyClick = (propertyId: number) => {
    setSelectedPropertyId(propertyId);
  };

  const handleBackClick = () => {
    setSelectedPropertyId(null);
    setStatusFilter('All');
    setSearchTerm('');
  };

  const selectedPropertyRequests = useMemo(() => {
    if (!selectedPropertyId) return [];
    return (
      groupedRequests[selectedPropertyId]?.requests.filter((req) => {
        const statusMatch =
          statusFilter === 'All' ||
          req.status.toLowerCase() ===
            statusFilter.toLowerCase().replace(' ', '-');
        const searchMatch =
          !searchTerm ||
          req.issueDescription.toLowerCase().includes(searchTerm.toLowerCase()); // Simplified search
        return statusMatch && searchMatch;
      }) || []
    );
  }, [selectedPropertyId, groupedRequests, statusFilter, searchTerm]);

  if (isLoading) {
    return <p>Loading requests...</p>;
  }

  if (selectedPropertyId) {
    const property = properties.find((p) => p.id === selectedPropertyId);
    return (
      <div>
        <div className="flex items-center gap-4 mb-6 border-b pb-4 border-gray-100">
          {/* 1. Improved Back Button: Distinct from text */}
          <button
            onClick={handleBackClick}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors border border-gray-200 hover:border-gray-300 group"
            aria-label="Go back to properties"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-500 group-hover:text-gray-800" />
          </button>

          {/* 2. Vertical Stack for Hierarchy */}
          <div className="flex flex-col">
            {/* Primary Heading: The "What" */}
            <h1 className="text-2xl font-bold text-gray-900 leading-tight">
              Maintenance Requests
            </h1>

            {/* Sub-heading: The "Where" (Context) */}
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <MapPinIcon className="w-4 h-4 mr-1 text-gray-400" />
              <span className="font-medium">
                {/* If your data includes "House at...", this displays it in a lighter font */}
                {property?.title || 'Loading property...'}
              </span>
            </div>
          </div>
        </div>

        <div className="mb-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="search"
                className="block text-sm font-medium text-gray-700"
              >
                Search by Issue Description
              </label>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Leaky faucet"
              />
            </div>
            <div>
              <label
                htmlFor="status"
                className="block text-sm font-medium text-gray-700"
              >
                Filter by Status
              </label>
              <select
                id="status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="All">All</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {selectedPropertyRequests.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center">
            <p className="text-sm text-gray-600">
              No matching maintenance requests found for this property.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {selectedPropertyRequests.map((req) => (
              <MaintenanceRequestCard
                key={req.id}
                request={req}
                userRole="landlord"
                onStatusChange={handleStatusChange}
                onDelete={handleDeleteRequest}
                isUpdating={isUpdating === req.id}
                isDeleting={isDeleting === req.id}
                propertyTitle={property?.title || ''}
              />
            ))}
          </div>
        )}
        {/* Pagination: Only show Load More when not filtered by property */}
        {/* Pagination controls for selected property view */}
        <div className="mt-6 flex justify-center gap-2">
          {page > 1 && (
            <button
              onClick={() => onPageChange && onPageChange(page - 1)}
              className="px-3 py-2 text-sm rounded-lg border bg-white hover:bg-gray-50"
            >
              Previous
            </button>
          )}
          <span className="px-3 py-2 text-sm text-gray-600">Page {page}</span>
          {hasNext && (
            <button
              onClick={() => onPageChange && onPageChange(page + 1)}
              className="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Next
            </button>
          )}
        </div>
        {isLoadingPage && (
          <div className="mt-2 text-center text-sm text-gray-500">Loading…</div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <button
          onClick={() => window.history.back()}
          className="p-2 rounded-full hover:bg-gray-100 mr-2"
          aria-label="Go back"
        >
          <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
        </button>
        <h2 className="text-2xl font-bold">Maintenance Requests by Property</h2>
      </div>
      {Object.keys(groupedRequests).length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
            <ClipboardDocumentListIcon className="h-6 w-6 text-gray-500" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">
            No Maintenance Requests Yet
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            You don’t have any maintenance requests across your properties. When
            tenants submit issues, they’ll appear here for quick triage.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => {
                setStatusFilter('All');
                setSearchTerm('');
              }}
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Reset Filters
            </button>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Refresh
            </button>
          </div>
          <div className="mt-4 text-xs text-gray-500">
            Tip: You can filter by status or search issue descriptions above.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.values(groupedRequests).map(
            ({ property, requests, pendingCount, inProgressCount }) => (
              <PropertyMaintenanceSummaryCard
                key={property.id}
                property={property}
                requestsCount={requests.length}
                pendingCount={pendingCount}
                inProgressCount={inProgressCount}
                onClick={() => handlePropertyClick(property.id)}
              />
            )
          )}
        </div>
      )}
      {/* Pagination controls when viewing all properties */}
      {!selectedPropertyId && (
        <div className="mt-6 flex justify-center gap-2">
          {page > 1 && (
            <button
              onClick={() => onPageChange && onPageChange(page - 1)}
              className="px-3 py-2 text-sm rounded-lg border bg-white hover:bg-gray-50"
            >
              Previous
            </button>
          )}
          <span className="px-3 py-2 text-sm text-gray-600">Page {page}</span>
          {hasNext && (
            <button
              onClick={() => onPageChange && onPageChange(page + 1)}
              className="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Next
            </button>
          )}
        </div>
      )}
      {isLoadingPage && !selectedPropertyId && (
        <div className="mt-2 text-center text-sm text-gray-500">Loading…</div>
      )}
    </div>
  );
};

export default LandlordMaintenanceView;
